import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    DragOverEvent,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    rectIntersection,
    type CollisionDetection,
} from '@dnd-kit/core';
import { Clock, Play, CheckCircle } from 'lucide-react';
import { Container, Header, BoardContainer, Coluna, ColunaHeader, DropZone, Card } from './styles';
import ColunaPendencias from './ColunaPendencias';
import CardPendencia from './CardPendencia';
import ModalVisualizarPendencia from './ModalVisualizarPendencia';
import ModalConcluirPendencia from './ModalConcluirPendencia';
import { PendenciaKanban, StatusKanbanType, ResultadoType, ColunaKanban, STATUS_CONCLUIDAS } from './types';
import toast from 'react-hot-toast';
import { toastDefs, toastDefsError } from '../../../config/toastConfig';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { CORES_COLUNAS } from './utils';
import { fetchPendenciasKanban } from './services';
import api from '../../../services/api';
import AbaErro from '../../Demanda/Common/AbaErro';
import AbaAcessoNegado from '../../Demanda/Common/AbaAcessoNegado';
import { isAcessoNegado } from '../../Demanda/Common/utils';
import { useAuth } from '../../../contexts/UserContext';

interface KanbanPendenciasProps {
    id_proposta?: number;
    onRefresh?: () => void;
    onNavigateToFile?: (fileId: number) => void;
}

const customCollisionDetection: CollisionDetection = (args) => {
    const columnIds = ['Pendentes', 'Em Andamento', 'Concluídas'];
    const columnsCollisions = args.droppableContainers.filter((container) => 
        columnIds.includes(container.id as string)
    );

    if (columnsCollisions.length > 0) {
        const columnIntersections = rectIntersection({
            ...args,
            droppableContainers: columnsCollisions,
        });
        if (columnIntersections?.length > 0) {
            return columnIntersections;
        }
    }

    return rectIntersection(args);
};

const KanbanPendencias: React.FC<KanbanPendenciasProps> = ({ id_proposta, onRefresh, onNavigateToFile }) => {
    const { user } = useAuth();
    const [pendencias, setPendencias] = useState<PendenciaKanban[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);
    const [filtroResultado, setFiltroResultado] = useState<ResultadoType>(null);
    const [isModalViewOpen, setIsModalViewOpen] = useState(false);
    const [pendenciaSelecionada, setPendenciaSelecionada] = useState<PendenciaKanban | null>(null);
    const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
    const [isModalConcluirOpen, setIsModalConcluirOpen] = useState(false);
    const [pendenciaParaConcluir, setPendenciaParaConcluir] = useState<PendenciaKanban | null>(null);
    const [statusAnterior, setStatusAnterior] = useState<StatusKanbanType | null>(null);
    const isMountedRef = useRef<boolean>(true);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 3,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 8,
            },
        })
    );

    const loadPendencias = useCallback(async () => {
        if (!id_proposta) {
            setPendencias([]);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const pendenciasCarregadas = await fetchPendenciasKanban(id_proposta);
            
            const userProfile = user?.profile;
            const userId = user?.id ? Number(user.id) : null;
            
            const pendenciasFiltradas = pendenciasCarregadas.filter(pendencia => {
                const destinoNormalizado = String(pendencia.destino || '').toLowerCase().trim();
                const tipoResponsavel = pendencia.tipo_responsavel;
                
                if (userProfile === 'comum' && userId) {
                    if (tipoResponsavel === 'Solicitante') {
                        return false;
                    }
                    
                    if (destinoNormalizado === 'solicitante') {
                        return false;
                    }
                    
                    const podeVer = 
                        (tipoResponsavel === 'Profissional' && pendencia.id_responsavel === userId) ||
                        (destinoNormalizado === 'profissional' && pendencia.id_destino === userId && tipoResponsavel === 'Operador');
                    
                    return podeVer;
                }
                
                if (userProfile === 'Solicitante' || userProfile === 'solicitante') {
                    if (tipoResponsavel === 'Profissional') {
                        return false;
                    }
                    
                    if (destinoNormalizado === 'profissional') {
                        return false;
                    }
                    
                    return true;
                }
                
                return true;
            });
            
            setPendencias(pendenciasFiltradas);
        } catch (err: any) {
            const errorMessage = err.message || 'Não foi possível carregar as pendências. Tente novamente.';
            if (isAcessoNegado(err)) {
                setError('acesso_negado');
            } else {
                setError(errorMessage);
                toast.error(errorMessage, toastDefsError);
            }
            setPendencias([]);
        } finally {
            setLoading(false);
        }
    }, [id_proposta, user]);

    useEffect(() => {
        isMountedRef.current = true;
        loadPendencias();
        
        return () => {
            isMountedRef.current = false;
        };
    }, [loadPendencias]);

    const colunas: ColunaKanban[] = useMemo(() => [
        {
            id: 'Pendentes',
            title: 'Pendentes',
            icon: Clock,
            bgColor: CORES_COLUNAS.PENDENTES.bg,
            iconColor: CORES_COLUNAS.PENDENTES.icon,
        },
        {
            id: 'Em Andamento',
            title: 'Em Andamento',
            icon: Play,
            bgColor: CORES_COLUNAS.EM_ANDAMENTO.bg,
            iconColor: CORES_COLUNAS.EM_ANDAMENTO.icon,
        },
        {
            id: STATUS_CONCLUIDAS,
            title: 'Concluídas',
            icon: CheckCircle,
            bgColor: CORES_COLUNAS.CONCLUIDAS.bg,
            iconColor: CORES_COLUNAS.CONCLUIDAS.icon,
        },
    ], []);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    }, []);

    const handleDragOver = useCallback((event: DragOverEvent) => {
        setOverId(event.over?.id as string || null);
    }, []);

    const resetDragState = useCallback(() => {
        setActiveId(null);
        setOverId(null);
    }, []);

    const mapStatusToBackend = (status: StatusKanbanType): string => {
        const mapping: Record<StatusKanbanType, string> = {
            'Pendentes': 'pendente',
            'Em Andamento': 'em_andamento',
            'Concluídas': 'concluida'
        };
        return mapping[status];
    };

    const revertStatusLocal = useCallback((id_pendencia: number, newStatus: StatusKanbanType) => {
        setPendencias(prevPendencias => 
            prevPendencias.map(p =>
                p.id_pendencia === id_pendencia
                    ? { ...p, status_kanban: newStatus }
                    : p
            )
        );
    }, []);

    const handleUpdateStatus = useCallback(async (id_pendencia: number, newStatus: StatusKanbanType) => {
        setUpdatingStatus(id_pendencia);
        
        const statusNames: Record<StatusKanbanType, string> = {
            'Pendentes': 'Pendentes',
            'Em Andamento': 'Em Andamento',
            'Concluídas': 'Concluídas'
        };

        try {
            const statusBackend = mapStatusToBackend(newStatus);
            const response = await api.put(`/pendencias/${id_pendencia}/status`, {
                status_kanban: statusBackend
            });

            if (!isMountedRef.current) return;

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Erro ao atualizar status');
            }

            if (!isMountedRef.current) return;

            toast.success(`Status atualizado - Pendência movida para ${statusNames[newStatus]}`, toastDefs);
            
            setPendencias(prevPendencias => 
                prevPendencias.map(p =>
                    p.id_pendencia === id_pendencia
                        ? { ...p, status_kanban: newStatus }
                        : p
                )
            );
        } catch (error: any) {
            if (!isMountedRef.current) return;
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar status da pendência';
            toast.error(errorMessage, toastDefsError);
        } finally {
            setUpdatingStatus(null);
        }
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) {
            resetDragState();
            return;
        }

        const activeIdStr = active.id as string;
        const overIdStr = over.id as string;

        const activePendencia = pendencias.find(
            p => p.id_pendencia.toString() === activeIdStr
        );

        if (!activePendencia) {
            resetDragState();
            return;
        }

        let newStatus: StatusKanbanType = activePendencia.status_kanban;

        const targetColumn = colunas.find(col => col.id === overIdStr);

        if (targetColumn) {
            newStatus = targetColumn.id as StatusKanbanType;
        } else {
            const targetPendencia = pendencias.find(
                p => p.id_pendencia.toString() === overIdStr
            );
            if (targetPendencia) {
                newStatus = targetPendencia.status_kanban;
            } else {
                resetDragState();
                return;
            }
        }

        if (newStatus !== activePendencia.status_kanban) {
            if (newStatus === STATUS_CONCLUIDAS) {
                setPendenciaParaConcluir(activePendencia);
                setStatusAnterior(activePendencia.status_kanban);
                revertStatusLocal(activePendencia.id_pendencia, newStatus);
                setIsModalConcluirOpen(true);
            } else {
                handleUpdateStatus(activePendencia.id_pendencia, newStatus);
            }
        }

        resetDragState();
    }, [pendencias, colunas, resetDragState, handleUpdateStatus]);

    const getPendenciasByStatus = useCallback((status: StatusKanbanType) => {
        let filtered = pendencias.filter(pendencia => pendencia.status_kanban === status);
        
        if (status === STATUS_CONCLUIDAS && filtroResultado) {
            filtered = filtered.filter(p => p.resultado === filtroResultado);
        }
        
        return filtered;
    }, [pendencias, filtroResultado]);

    // Total de pendências concluídas (sem filtro de resultado) - para manter botões visíveis
    const totalConcluidasSemFiltro = useMemo(() => {
        return pendencias.filter(pendencia => pendencia.status_kanban === STATUS_CONCLUIDAS).length;
    }, [pendencias]);

    const todasPendenciasConcluidas = useMemo(() => {
        return pendencias.filter(pendencia => pendencia.status_kanban === STATUS_CONCLUIDAS);
    }, [pendencias]);

    const activePendencia = useMemo(() => 
        pendencias.find(p => p.id_pendencia.toString() === activeId),
        [pendencias, activeId]
    );

    const handleViewPendencia = useCallback((pendencia: PendenciaKanban) => {
        if (!activeId) {
            setPendenciaSelecionada(pendencia);
            setIsModalViewOpen(true);
        }
    }, [activeId]);

    const handleFilterResultado = useCallback((resultado: ResultadoType) => {
        setFiltroResultado(prev => prev === resultado ? null : resultado);
    }, []);

    if (loading) {
        return (
            <Container>
                <Header>
                    <p>Carregando pendências...</p>
                </Header>
                <BoardContainer>
                    {[1, 2, 3].map((index) => (
                        <Coluna key={index} bgColor="transparent">
                            <ColunaHeader bgColor="transparent">
                                <div className="header-content">
                                    <div className="title-group">
                                        <Skeleton circle width={16} height={16} />
                                        <Skeleton width={100} height={16} />
                                    </div>
                                    <Skeleton width={24} height={20} borderRadius={10} />
                                </div>
                            </ColunaHeader>
                            <DropZone>
                                {[1, 2, 3].map((cardIndex) => (
                                    <Card key={cardIndex} style={{ padding: '14px' }}>
                                        <div className="card-header">
                                            <Skeleton width={80} height={24} borderRadius={4} />
                                            <Skeleton width={40} height={14} />
                                        </div>
                                        <div className="card-content">
                                            <Skeleton count={3} height={14} />
                                        </div>
                                        <div className="card-footer">
                                            <Skeleton width={100} height={12} />
                                        </div>
                                    </Card>
                                ))}
                            </DropZone>
                        </Coluna>
                    ))}
                </BoardContainer>
            </Container>
        );
    }

    if (error === 'acesso_negado') {
        return <AbaAcessoNegado />;
    }

    if (error) {
        return <AbaErro mensagem="Não foi possível carregar as pendências." onRetry={loadPendencias} />;
    }

    return (
        <Container>
            <DndContext
                sensors={sensors}
                collisionDetection={customCollisionDetection}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <BoardContainer>
                    {colunas.map((coluna) => {
                        const pendenciasColuna = getPendenciasByStatus(coluna.id);
                        const isConcluidas = coluna.id === STATUS_CONCLUIDAS;
                        
                        const isOver = overId === coluna.id || 
                            (overId ? pendenciasColuna.some(
                                p => p.id_pendencia.toString() === overId
                            ) : false);
                        
                        return (
                            <ColunaPendencias
                                key={coluna.id}
                                coluna={coluna}
                                pendencias={pendenciasColuna}
                                isOver={isOver}
                                onFilterResultado={isConcluidas ? handleFilterResultado : undefined}
                                filtroAtivo={isConcluidas ? filtroResultado : undefined}
                                onView={handleViewPendencia}
                                totalConcluidasSemFiltro={isConcluidas ? totalConcluidasSemFiltro : undefined}
                                todasPendenciasConcluidas={isConcluidas ? todasPendenciasConcluidas : undefined}
                                userProfile={user?.profile}
                                userId={user?.id ? Number(user.id) : null}
                                idEscritorioSolicitante={user?.id_escritorio_solicitante || null}
                            />
                        );
                    })}
                </BoardContainer>

                <DragOverlay>
                    {activePendencia ? (
                        <CardPendencia pendencia={activePendencia} />
                    ) : null}
                </DragOverlay>
            </DndContext>

            <ModalVisualizarPendencia
                modalAtivo={isModalViewOpen}
                setModalAtivo={setIsModalViewOpen}
                pendencia={pendenciaSelecionada}
                onNavigateToFile={onNavigateToFile}
            />

            <ModalConcluirPendencia
                modalAtivo={isModalConcluirOpen}
                setModalAtivo={setIsModalConcluirOpen}
                pendencia={pendenciaParaConcluir}
                statusAnterior={statusAnterior}
                onConcluida={() => {
                    loadPendencias();
                    onRefresh?.();
                }}
                onCancelar={() => {
                    if (pendenciaParaConcluir && statusAnterior) {
                        revertStatusLocal(pendenciaParaConcluir.id_pendencia, statusAnterior);
                    }
                }}
            />
        </Container>
    );
};

export default KanbanPendencias;

